import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { useMusic } from '../../context/MusicContext';
import { useProfile } from '../../context/ProfileContext';

// Import your display components
import RegularUserProfile from './sections/RegularUserProfile';
import ArtistProfile from './sections/ArtistProfile';
import ProfileHeader from '../../components/profile/ProfileHeader';

const PublicProfilePage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useProfile();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setQueueAndPlay } = useMusic();
  const [playlists, setPlaylists] = useState([]);

  const [posts, setPosts] = useState([]);
  const [isPostsLoading, setIsPostsLoading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!profileId) {
        setError("No profile ID specified.");
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const db = getFirestore();
      
      // Check artists collection first
      const artistRef = doc(db, "artist_accounts", profileId);
      const artistSnap = await getDoc(artistRef);
      if (artistSnap.exists()) {
        setProfile({ id: artistSnap.id, type: 'artist', ...artistSnap.data() });
      } else {
        // If not an artist, check users collection
        const userRef = doc(db, "users", profileId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setProfile({ id: userSnap.id, type: 'user', ...userSnap.data() });
        } else {
          setError("Profile not found.");
        }
      }
      setIsLoading(false);
    };

    fetchProfile();
  }, [profileId]);

  useEffect(() => {
    if (profile?.type !== 'user') {
      setPlaylists([]);
      return;
    }

    const fetchUserPlaylists = async () => {
      const db = getFirestore();
      try {
        const playlistsRef = collection(db, "playlists");
        const q = query(playlistsRef, where("ownerId", "==", profile.id));
        const querySnapshot = await getDocs(q);

        const publicPlaylists = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(playlist => playlist.isPrivate !== true); // 🌟 Blocks true, allows false and legacy blanks!

        setPlaylists(publicPlaylists);
      } catch (err) {
        console.error("Error fetching user playlists:", err);
      }
    };

    fetchUserPlaylists();
  }, [profile]);

  // ✅ 3. ADD A NEW useEffect TO FETCH POSTS
  useEffect(() => {
    // Only try to fetch posts if the loaded profile is an artist
    if (profile?.type !== 'artist') {
        setPosts([]); // Ensure posts are empty for non-artist profiles
        return;
    }

    const fetchPosts = async () => {
          setIsPostsLoading(true);
          const db = getFirestore();
          try {
              const postsRef = collection(db, "artistPosts");
              const q = query(
                  postsRef,
                  where("artistId", "==", profile.id),
                  orderBy("createdAt", "desc")
              );
              const querySnapshot = await getDocs(q);
              const fetchedPosts = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              
              const visiblePosts = fetchedPosts.filter(post => !post.deletedAt);
              setPosts(visiblePosts);
          } catch (error) {
              console.error("Error fetching public posts:", error);
          } finally {
              setIsPostsLoading(false);
          }
      };

      fetchPosts();
    }, [profile]);

  if (isLoading) return <p className="text-center mt-8">Loading...</p>;
  if (error) return <p className="text-center text-red-500 mt-8">{error}</p>;
  if (!profile) return <p className="text-center mt-8">Profile not found.</p>;

  // This page is ALWAYS a public view
  return (
    <div className={`relative min-h-screen p-4 pb-32 transition-colors duration-500 ${isDarkMode ? 'bg-brand-black text-white' : 'bg-white'}`}>
      <ProfileHeader profile={profile} isOwnProfile={false} />

      <div className="mt-4">
        {profile.type === 'artist' 
          ? <ArtistProfile 
              artistProfile={profile} 
              isPublicView={true}
              setQueueAndPlay={setQueueAndPlay}
              posts={posts}
              isPostsLoading={isPostsLoading}
            />
          : <RegularUserProfile 
              userProfile={profile} 
              playlists={playlists} 
              isPublicView={true} 
              onPlaylistClick={(playlist) => navigate(`/playlist/${playlist.id}`)}
            />
        }
      </div>
    </div>
  );
};

export default PublicProfilePage;